let _logoDataUrl = null; 

const fmtDateTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return String(iso); }
};

const DEFAULT_COLUMN_WIDTHS = {
  id: 150,        
  version: 55,    
  type: 70,       
  status: 80,     
  title: '*',     
  createdAt: 115,
  updatedAt: 115,
  publishedAt: 115,
};

const brand = {
  primary: '#1462A5',
  headerText: '#FFFFFF',
  zebra: '#F6FAFF',
  border: '#D9E3F0',
};

const buildWidths = (EXPORT_ORDER, columnWidths) => {
  const cfg = { ...DEFAULT_COLUMN_WIDTHS, ...(columnWidths || {}) };
  return EXPORT_ORDER.map(col => cfg[col.key] ?? '*');
};

const loadImageAsDataURL = async (url) => {
  if (!url) return null;
  if (_logoDataUrl) return _logoDataUrl;

  const res = await fetch(url, { cache: 'no-store' });
  const blob = await res.blob();
  const dataUrl = await new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(blob);
  });

  _logoDataUrl = dataUrl;
  return dataUrl;
};

const makeTableBody = (rows, EXPORT_ORDER) => {
  const headerRow = EXPORT_ORDER.map(o => ({
    text: o.label, bold: true, color: brand.headerText
  }));
  const bodyRows = rows.map(r => EXPORT_ORDER.map(o => r[o.label] ?? ''));
  return [headerRow, ...bodyRows];
};

const buildPdfDoc = ({ title, subtitle, rows, EXPORT_ORDER, logoDataUrl, columnWidths }) => {
  const tableBody = makeTableBody(rows, EXPORT_ORDER);
  const widths    = buildWidths(EXPORT_ORDER, columnWidths);

  const headerColumns = logoDataUrl
  ? [
    { image: logoDataUrl, width: 80, margin: [0, -6, 12, 0] },
    {
      stack: [
        { text: title, style: 'title', margin: [0, -2, 0, 6] },
        { text: subtitle || '', style: 'subtitle', margin: [0, 2, 0, 0] },
      ],
      alignment: 'left',
    },
    { text: '', alignment: 'right' },
  ]
: [
    { text: title, style: 'title', margin: [0, -2, 0, 6] },
    { text: subtitle || '', style: 'subtitle', alignment: 'right', margin: [0, 2, 0, 0] },
  ];

return {
  info: {
    title,
    subject: subtitle || 'Relatório',
    author: 'Relatórios',
    keywords: 'relatórios, export, pdf',
  },

  pageSize: 'A4',
  pageOrientation: 'landscape',

  pageMargins: [28, 72, 28, 52],

  header: () => ({
    margin: [28, 10, 28, 0],
    columns: headerColumns,
  }),

  footer: (currentPage, pageCount) => ({
    margin: [28, 0, 28, 18],
    columns: [
      { text: fmtDateTime(new Date().toISOString()), style: 'footerNote' },
      { text: `Página ${currentPage} de ${pageCount}`, alignment: 'right', style: 'footerNote' }
    ]
  }),

  content: [
    {
      table: { headerRows: 1, widths, body: tableBody },
      layout: {
        hLineWidth: () => 0.6,
        vLineWidth: () => 0.6,
        hLineColor: () => brand.border,
        vLineColor: () => brand.border,
        paddingLeft: () => 6,
        paddingRight: () => 6,
        paddingTop: () => 4,
        paddingBottom: () => 4,
        fillColor: (rowIndex) => {
          if (rowIndex === 0) return brand.primary;
          return rowIndex % 2 === 0 ? brand.zebra : null;
        },
      },
    },
  ],

  styles: {
    title: { fontSize: 14, bold: true, color: brand.primary },
    subtitle: { fontSize: 9, color: '#64748B' },
    footerNote: { fontSize: 8, color: '#6B7280' },
  },
  defaultStyle: { fontSize: 9 },
};
};

const ensurePdfFonts = async (pdfMake) => {
  const pdfFonts = await import('pdfmake/build/vfs_fonts');
  pdfMake.vfs = pdfFonts?.pdfMake?.vfs || pdfFonts.default?.vfs || pdfFonts.vfs || pdfMake.vfs;
};

export const createPdfFromRows = async ({
  rows,
  EXPORT_ORDER,
  title = 'Relatório de vídeos',
  subtitle = '',
  filename = 'relatorio.pdf',
  logoPath, 
  columnWidths, 
}) => {
  const pdfMake = (await import('pdfmake/build/pdfmake')).default;
  await ensurePdfFonts(pdfMake);

  const logoDataUrl = logoPath ? await loadImageAsDataURL(logoPath) : null;

  const dd = buildPdfDoc({
    title,
    subtitle,
    rows,
    EXPORT_ORDER,
    logoDataUrl,
    columnWidths 
  });

  pdfMake.createPdf(dd).download(filename);
};
